import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { ForgotPasswordButton } from "../../ForgotPasswordButton";
import { useLanguage } from "../../Language";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import axios from "axios";
import { SuccessMessageControl } from "@tlc/ls2pac/app/controls/successMessageControl";
import { UpdateLocalUserReturn } from "./EditUsernamePage";
import { useAccount } from "../../Account";
import { useForm, SubmitHandler } from "react-hook-form";

interface EditPasswordFormInput {
  password: string;
  confirmPassword: string;
  oldPassword: string;
}

export const EditPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { translate } = useLanguage();
  const { user } = useAccount();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<EditPasswordFormInput>({
    mode: "onBlur",
  });
  const [errorMessage, setErrorMessage] = React.useState<string>(null);

  const onSubmit: SubmitHandler<EditPasswordFormInput> = async ({
    password,
    oldPassword,
  }) => {
    setErrorMessage(null);

    const { data } = await axios.post<UpdateLocalUserReturn>(
      `account/update/password`,
      {
        password,
        credentials: {
          username: user.username,
          password: oldPassword,
          useLocalLoginMethod: true,
        },
      }
    );

    if (data.success === true) {
      SuccessMessageControl.show(translate("successUpdatingEZPasswordMessage"));
      navigate("/myaccount/summary");
      return;
    }

    setErrorMessage(translate("errorUpdatingEZPasswordMessage"));
  };

  return (
    <>
      <div id="ezUsernameViewContainer"></div>
      <Toolbar disableGutters={true}>
        <IconButton
          aria-label="back"
          onClick={() => navigate("/myaccount/summary")}
        >
          <ArrowBackIosIcon />
        </IconButton>
        <Typography variant="h5">{translate("updateEZPassword")}</Typography>
      </Toolbar>
      <Box>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack direction="column" sx={{
            gap: 2
          }}>
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
            <TextField
              name="password"
              required
              {...register("password", {
                required: translate("passwordIsRequired"),
              })}
              error={!!errors.password}
              helperText={errors.password?.message || " "}
              label={translate("newEZPassword")}
              type="password"
            ></TextField>
            <TextField
              name="confirmPassword"
              required
              {...register("confirmPassword", {
                required: translate("confirmPasswordIsRequired"),
                validate: (value) =>
                  value === watch("password") ||
                  translate("passwordAndConfirmPasswordMustMatchError"),
              })}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message || " "}
              label={translate("confirmNewEZPassword")}
              type="password"
            ></TextField>
            <Divider />
            <TextField
              name="oldPassword"
              required
              {...register("oldPassword", {
                required: translate("oldPasswordIsRequired"),
              })}
              error={!!errors.oldPassword}
              helperText={
                errors.oldPassword?.message ||
                translate("enterOldPinForSecurityPurposes")
              }
              label={translate("oldEZPassword")}
              type="password"
            ></TextField>
            <Box>
              {/* TODO does this need a local config check? */}
              <ForgotPasswordButton />
            </Box>

            <Box>
              <Button type="submit" variant="contained">
                {translate("save")}
              </Button>
              <Button
                type="button"
                onClick={() => navigate("/myaccount/summary")}
              >
                {translate("cancel")}
              </Button>
            </Box>
          </Stack>
        </form>
      </Box>
    </>
  );
};
